import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Text from 'components/_UI/Text';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import {
    Heart, MapPin, Phone, Clock, Share2,
    Instagram, Twitter, Globe, Music,
    Wifi, CreditCard, Wind, Camera, Smile,
    Calendar, Coffee, Navigation, Star,
    ChevronRight, ShoppingBag, ExternalLink,
    Users, Thermometer, MessageCircle, X, UtensilsCrossed, Ticket,
    UserPlus, UserCheck,
} from 'lucide-react';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import establishmentTypes from 'theme/establishmentTypes';
import eventTypes from 'theme/eventTypes';
import establishmentsApi from 'api/requests/establishments';
import useShare from 'hooks/useShare';
import useFavorite from 'hooks/useFavorite';
import useFollow from 'hooks/useFollow';
import { useAuth } from 'context/auth/Auth';
import { useShowLoginModal } from 'context/general/ShowLoginModal';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
    monday: 'Seg', tuesday: 'Ter', wednesday: 'Qua', thursday: 'Qui',
    friday: 'Sex', saturday: 'Sáb', sunday: 'Dom',
};

function getFeatureIcon(iconKey, size = 15) {
    const c = colors.secondaryPurple;
    const map = {
        'wifi': <Wifi size={size} color={c} />,
        'smokingRoom': <Wind size={size} color={c} />,
        'meetingRoom': <Users size={size} color={c} />,
        'instagram': <Instagram size={size} color={c} />,
        'bike': <Navigation size={size} color={c} />,
        'cool': <Thermometer size={size} color={c} />,
        'creditCard': <CreditCard size={size} color={c} />,
        'parking': <Navigation size={size} color={c} />,
        'music': <Music size={size} color={c} />,
        'calendar': <Calendar size={size} color={c} />,
        'heart': <Heart size={size} color={c} />,
        'coffee': <Coffee size={size} color={c} />,
        'smile': <Smile size={size} color={c} />,
        'camera': <Camera size={size} color={c} />,
        'phone': <Phone size={size} color={c} />,
        'clock': <Clock size={size} color={c} />,
    };
    return map[iconKey] || <Star size={size} color={c} />;
}

const SOCIAL_ICON_MAP = {
    'instagram': <Instagram size={16} color={colors.secondaryPurple} />,
    'whatsapp': <MessageCircle size={16} color={colors.secondaryPurple} />,
    'twitter': <Twitter size={16} color={colors.secondaryPurple} />,
    'tiktok': <Music size={16} color={colors.secondaryPurple} />,
    'website': <Globe size={16} color={colors.secondaryPurple} />,
};

function safeWebsiteHref(url) {
    if (!url) return '#';
    if (/^javascript:/i.test(url)) return '#';
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
}

export default function EstablishmentDetailV2({ data, slug: slugProp }) {
    const resolvedSlug = slugProp || data?.slug;
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [products, setProducts] = useState([]);
    const [events, setEvents] = useState([]);
    const [options, setOptions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [linkCopied, setLinkCopied] = useState(false);
    const { share } = useShare();
    const linkCopiedTimerRef = useRef(null);
    const { isAuth, user } = useAuth();
    const { setShowLoginModal } = useShowLoginModal();
    const { isFollowing, toggleFollow } = useFollow(detail?.user_id, detail?.is_following);
    const isOwner = isAuth && user?.id === detail?.user_id;
    // TODO: Descomentar quando o endpoint de favoritar estabelecimentos estiver pronto
    // const { isFavorited, toggleFavorite } = useFavorite(detail?.slug || resolvedSlug, detail?.is_favorited || false, null, 'establishment');

    function handleFollow() {
        toggleFollow(() => setShowLoginModal(true));
    }

    useEffect(() => {
        let isMounted = true;
        async function fetchData() {
            if (!resolvedSlug) return;
            try {
                const [detailRes, productsRes, optionsRes, eventsRes] = await Promise.all([
                    establishmentsApi.getBySlug(resolvedSlug),
                    establishmentsApi.getProducts(resolvedSlug),
                    establishmentsApi.getOptions(),
                    establishmentsApi.getUpcomingEvents(resolvedSlug),
                ]);
                if (!isMounted) return;
                setDetail(detailRes.data?.data || detailRes.data);
                setProducts(productsRes.data?.data || productsRes.data || []);
                setOptions(optionsRes.data);
                setEvents(eventsRes.data?.data || eventsRes.data || []);
            } catch (err) {
                console.error('Error fetching establishment detail:', err);
            } finally {
                if (isMounted) setLoading(false);
            }
        }
        fetchData();
        return () => { isMounted = false; };
    }, [resolvedSlug]);

    useEffect(() => () => clearTimeout(linkCopiedTimerRef.current), []);

    async function handleShare() {
        const result = await share({
            title: detail?.name || data?.name,
            text: `Conheça ${detail?.name || data?.name}`,
            url: window.location.href,
        });
        if (result?.method === 'clipboard' && result?.success) {
            clearTimeout(linkCopiedTimerRef.current);
            setLinkCopied(true);
            linkCopiedTimerRef.current = setTimeout(() => setLinkCopied(false), 2500);
        }
    }

    if (loading) {
        return (
            <FullyCentered height='300px'>
                <Loader />
            </FullyCentered>
        );
    }

    const isOpen = detail?.open;
    const name = detail?.name || data?.name;
    const profileImg = detail?.profileImage?.path || data?.profileImage?.path;
    const backgroundImg = detail?.backgroundImage?.path || data?.backgroundImage?.path;

    const addressPrimary = [detail?.address?.name, detail?.address?.number].filter(Boolean).join(', ');
    const addressSecondary = [detail?.address?.district, detail?.address?.city].filter(Boolean).join(', ');
    const addressHeader = [detail?.address?.name, detail?.address?.district, detail?.address?.city].filter(Boolean).join(', ');

    const classificationId = detail?.classification?.id;
    const isFree = classificationId != null ? establishmentTypes?.free?.id == classificationId : null;

    const stats = [
        { label: 'Avaliações', value: detail?.statistics?.reviews },
        { label: 'Produtos', value: detail?.statistics?.products },
        { label: 'Eventos', value: detail?.statistics?.events },
        { label: 'Seguidores', value: detail?.statistics?.followers },
    ].filter(s => s.value != null);

    const socials = (detail?.socialMedia || []).map(sm => ({
        key: String(sm.id),
        icon: SOCIAL_ICON_MAP[sm.icon?.toLowerCase()] || <Globe size={16} color={colors.secondaryPurple} />,
        label: sm.name,
        href: safeWebsiteHref(sm.url),
    }));

    const hasDates = detail?.dates && DAY_KEYS.some(day => detail.dates[day] !== undefined);
    const hasServiceOptions = !!detail?.serviceOptions?.length && !!options?.serviceOptions?.length;

    return (
        <DetailContainer>
            {/* ── HEADER: centered hero layout ── */}
            <Header>
                <HeroBanner>
                    <HeroImage
                        src={backgroundImg || '/assets/images/placeholder-background.png'}
                        alt=''
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/placeholder-background.png'; }}
                    />
                    <HeroOverlay />
                    <HeroActions>
                        {!!products?.length && (
                            <ActionButton as='a' href={`/estabelecimento/${detail?.slug || data?.slug}`} target='_blank' rel='noopener noreferrer'>
                                <ExternalLink size={15} color={colors.neutralLight} />
                                <ActionLabel>Ver página</ActionLabel>
                            </ActionButton>
                        )}
                        {/* TODO: Descomentar quando o endpoint de favoritar estabelecimentos estiver pronto
                        <ActionButton onClick={toggleFavorite}>
                            <Heart size={15} color={colors.neutralLight} fill={isFavorited ? colors.neutralLight : 'none'} />
                            <ActionLabel>Favoritar</ActionLabel>
                        </ActionButton> */}
                        {!isOwner && (
                            <ActionButton onClick={handleFollow}>
                                {isFollowing ? (
                                    <UserCheck size={15} color={colors.neutralLight} />
                                ) : (
                                    <UserPlus size={15} color={colors.neutralLight} />
                                )}
                                <ActionLabel>{isFollowing ? 'Seguindo' : 'Seguir'}</ActionLabel>
                            </ActionButton>
                        )}
                        <ActionButton onClick={handleShare}>
                            <Share2 size={15} color={colors.neutralLight} />
                            <ActionLabel>{linkCopied ? 'Copiado!' : 'Compartilhar'}</ActionLabel>
                        </ActionButton>
                    </HeroActions>
                </HeroBanner>

                <HeaderBody>
                    <AvatarWrapper>
                        <AvatarRing isOpen={isOpen}>
                            <DynamicProfileImage img={profileImg} word={name} size='80px' textSize='subtitle' />
                        </AvatarRing>
                        <AvatarStatusBadge isOpen={isOpen}>{isOpen ? 'Aberto' : 'Fechado'}</AvatarStatusBadge>
                    </AvatarWrapper>

                    <HeaderName>{name}</HeaderName>

                    {!!detail?.rating && (
                        <HeaderMetaRow>
                            <RatingText>⭐ {detail.rating}{detail?.ratings_count ? ` (${detail.ratings_count})` : ''}</RatingText>
                        </HeaderMetaRow>
                    )}

                    <Text>{detail?.description}</Text>

                    {(isFree !== null || detail?.adulthood || detail?.tagsEstablishments?.length > 0) && (
                        <TagsRow>
                            {isFree !== null && (
                                <ClassificationChip isFree={isFree}>
                                    {isFree
                                        ? (establishmentTypes?.free?.label || 'Gratuito')
                                        : (establishmentTypes?.paid?.label || 'Pago')}
                                </ClassificationChip>
                            )}
                            {detail?.adulthood && <AdulthoodChip>+18</AdulthoodChip>}
                            {detail?.tagsEstablishments?.map(tag => (
                                <TagChip key={tag.name}>{tag.name}</TagChip>
                            ))}
                        </TagsRow>
                    )}

                    <MenuCTAButton href={`/${detail?.slug || resolvedSlug}/menu`}>
                        <UtensilsCrossed size={17} color={colors.neutralLight} />
                        Ver Cardápio
                    </MenuCTAButton>
                </HeaderBody>
            </Header>

            {/* ── TWO-COLUMN CONTENT ── */}
            <ContentArea>
                {/* LEFT COLUMN */}
                <LeftColumn>
                    {/* Upcoming Events */}
                    {!!events?.length && (
                        <Section>
                            <SectionTitleRow>
                                <Calendar size={18} color={colors.secondaryPurple} />
                                <Text name='subtitle' type='bold' color={colors.secondaryPurple} paddingBottom={0} marginLeft='8px'>
                                    Próximos Eventos
                                </Text>
                            </SectionTitleRow>
                            <EventsScroll>
                                {events.map(event => {
                                    const firstDate = event?.dates?.[0];
                                    const dateLabel = firstDate?.date
                                        ? new Date(firstDate.date + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                                        : null;
                                    const timeLabel = firstDate?.start ? firstDate.start.slice(0, 5) : null;
                                    const thumb = event?.thumb || event?.images?.[0];
                                    const classificationName = event?.classification?.name;
                                    const isFreeEvent = classificationName === eventTypes.free.slug;
                                    const isPaidEvent = classificationName === eventTypes.paid.slug;

                                    return (
                                        <EventItem
                                            key={event.slug}
                                            href={`/evento/${event.slug}`}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            <EventThumb>
                                                <img
                                                    src={thumb || '/assets/images/placeholder-events.png'}
                                                    alt={event.name}
                                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/placeholder-events.png'; }}
                                                />
                                                {event.has_tickets && (
                                                    <EventTicketBadge>
                                                        <Ticket size={10} color={colors.neutralLight} />
                                                    </EventTicketBadge>
                                                )}
                                            </EventThumb>
                                            <EventItemInfo>
                                                <EventItemName>{event.name}</EventItemName>
                                                {(isFreeEvent || isPaidEvent) && (
                                                    <EventPriceBadge isFree={isFreeEvent}>
                                                        {isFreeEvent ? 'Grátis' : 'Pago'}
                                                    </EventPriceBadge>
                                                )}
                                                {(dateLabel || timeLabel) && (
                                                    <EventDateRow>
                                                        <Calendar size={11} color={colors.nightLight} />
                                                        <EventDateText>{[dateLabel, timeLabel].filter(Boolean).join(' · ')}</EventDateText>
                                                    </EventDateRow>
                                                )}
                                                {!event?.hide_address && event?.address?.street?.name && (
                                                    <EventDateRow>
                                                        <MapPin size={11} color={colors.nightLight} />
                                                        <EventDateText>
                                                            {[
                                                                event.address.street.name,
                                                                event.address.number && `nº ${event.address.number}`,
                                                                event.address.street.district,
                                                            ].filter(Boolean).join(', ')}
                                                        </EventDateText>
                                                    </EventDateRow>
                                                )}
                                            </EventItemInfo>
                                        </EventItem>
                                    );
                                })}
                            </EventsScroll>
                        </Section>
                    )}

                    {/* Products */}
                    {!!products?.length && (
                        <Section>
                            <SectionTitleRow>
                                <ShoppingBag size={18} color={colors.secondaryPurple} />
                                <Text name='subtitle' type='bold' color={colors.secondaryPurple} paddingBottom={0} marginLeft='8px'>
                                    Produtos em Destaque
                                </Text>
                            </SectionTitleRow>
                            <ProductsGrid>
                                {products.slice(0, 6).map(product => (
                                    <ProductCard key={product?.id || product?.slug} onClick={() => navigate(`/estabelecimento/${detail?.slug || resolvedSlug}`)}>
                                        <ProductImage>
                                            {product?.image?.path
                                                ? <img src={product.image.path} alt={product?.name} />
                                                : <ProductImagePlaceholder />
                                            }
                                        </ProductImage>
                                        <ProductInfo>
                                            <Text name='small' type='bold' paddingBottom='2px'>{product?.name}</Text>
                                            <Text name='mini' color={colors.nightLight} paddingBottom='4px'>{product?.category?.name}</Text>
                                            {product?.price != null && (
                                                <Text name='small' type='bold' color={colors.secondaryPurple} paddingBottom={0}>
                                                    R$ {Number(product.price).toFixed(2).replace('.', ',')}
                                                </Text>
                                            )}
                                        </ProductInfo>
                                    </ProductCard>
                                ))}
                            </ProductsGrid>
                            <FullyCentered marginTop='12px'>
                                <Button category='minimal' onClick={() => navigate(`/estabelecimento/${detail?.slug || resolvedSlug}`)}>
                                    <Text paddingBottom={0} type='bold' name='normal' color={colors.secondaryPurple}>
                                        Ver Cardápio Completo
                                    </Text>
                                    <ChevronRight size={16} color={colors.secondaryPurple} />
                                </Button>
                            </FullyCentered>
                        </Section>
                    )}

                    {/* Features — horizontal chips */}
                    {!!detail?.features?.length && (
                        <Section>
                            <SectionTitleRow>
                                <Text name='subtitle' type='bold' color={colors.secondaryPurple} paddingBottom={0}>
                                    Características do Estabelecimento
                                </Text>
                            </SectionTitleRow>
                            <ChipsRow>
                                {detail.features.map(feature => (
                                    <FeatureChip key={feature.id ?? feature.name}>
                                        {getFeatureIcon(feature.icon)}
                                        <ChipLabel>{feature.name}</ChipLabel>
                                    </FeatureChip>
                                ))}
                            </ChipsRow>
                        </Section>
                    )}

                    {/* Service Options — horizontal chips */}
                    {hasServiceOptions && (
                        <Section>
                            <SectionTitleRow>
                                <Text name='subtitle' type='bold' color={colors.secondaryPurple} paddingBottom={0}>
                                    Opções de Serviços
                                </Text>
                            </SectionTitleRow>
                            <ChipsRow>
                                {options.serviceOptions.map(service => {
                                    const selected = detail.serviceOptions.some(s => s.id === service.id);
                                    return (
                                        <ServiceChip key={service.id} selected={selected}>
                                            <ServiceDot selected={selected} />
                                            <ChipLabel faded={!selected}>{service.name}</ChipLabel>
                                        </ServiceChip>
                                    );
                                })}
                            </ChipsRow>
                        </Section>
                    )}

                    {/* Social networks */}
                    {!!socials.length && (
                        <Section>
                            <SectionTitleRow>
                                <Text name='subtitle' type='bold' color={colors.secondaryPurple} paddingBottom={0}>
                                    Redes Sociais
                                </Text>
                            </SectionTitleRow>
                            <SocialsGrid>
                                {socials.map((social) => (
                                    <SocialRow key={social.key} as='a' href={social.href} target='_blank' rel='noopener noreferrer'>
                                        {social.icon}
                                        <Text name='small' paddingBottom={0} marginLeft='10px'>{social.label}</Text>
                                    </SocialRow>
                                ))}
                            </SocialsGrid>
                        </Section>
                    )}
                </LeftColumn>

                {/* RIGHT SIDEBAR */}
                <RightSidebar>
                    {/* Contact info */}
                    {(detail?.phone || detail?.address) && (
                        <SideSection>
                            <Text name='subtitle' type='bold' color={colors.secondaryPurple} paddingBottom='12px'>
                                Informações de Contato
                            </Text>
                            {detail?.phone && (
                                <ContactRow>
                                    <Phone size={16} color={colors.secondaryPurple} />
                                    <ContactInfo>
                                        <Text name='small' type='bold' paddingBottom='2px'>{detail.phone}</Text>
                                        <Text name='mini' color={colors.nightLight} paddingBottom={0}>Telefone principal</Text>
                                    </ContactInfo>
                                </ContactRow>
                            )}
                            {detail?.address && (
                                <ContactRow>
                                    <MapPin size={16} color={colors.secondaryPurple} />
                                    <ContactInfo>
                                        {addressPrimary && (
                                            <Text name='small' type='bold' paddingBottom='2px'>
                                                {addressPrimary}{detail.address?.complement ? ` - ${detail.address.complement}` : ''}
                                            </Text>
                                        )}
                                        {addressSecondary && (
                                            <Text name='mini' color={colors.nightLight} paddingBottom={detail?.link_map ? '4px' : 0}>
                                                {addressSecondary}
                                            </Text>
                                        )}
                                        {detail?.link_map && (
                                            <MapLink href={detail.link_map} target='_blank' rel='noopener noreferrer'>
                                                Ver no mapa →
                                            </MapLink>
                                        )}
                                    </ContactInfo>
                                </ContactRow>
                            )}
                        </SideSection>
                    )}

                    {/* Opening hours — null-safe rendering */}
                    {hasDates && (
                        <SideSection>
                            <Row marginBottom='12px' verticalCenter>
                                <Clock size={16} color={colors.secondaryPurple} />
                                <Text name='subtitle' type='bold' color={colors.secondaryPurple} paddingBottom={0} marginLeft='8px'>
                                    Horários de Funcionamento
                                </Text>
                            </Row>
                            {DAY_KEYS.map(day => {
                                const slots = detail.dates[day];
                                const isClosed = !slots || slots.length === 0;
                                const is24h = Array.isArray(slots) && slots.some(s => s['24h']);
                                const timeStr = isClosed
                                    ? 'Fechado'
                                    : is24h
                                        ? '24 horas'
                                        : slots.filter(s => s.start).map(s => `${s.start}${s.end ? ` — ${s.end}` : ''}`).join(' / ');
                                return (
                                    <HoursRow key={day} isClosed={isClosed}>
                                        <DayLabel>{DAY_LABELS[day]}</DayLabel>
                                        <Text name='small' color={isClosed ? colors.nightLight : colors.nightDark} paddingBottom={0}>
                                            {timeStr}
                                        </Text>
                                    </HoursRow>
                                );
                            })}
                        </SideSection>
                    )}

                    {/* Statistics */}
                    {!!stats.length && (
                        <SideSection>
                            <Text name='subtitle' type='bold' color={colors.secondaryPurple} paddingBottom='12px'>
                                Estatísticas
                            </Text>
                            {stats.map(stat => (
                                <StatRow key={stat.label}>
                                    <Text name='small' paddingBottom={0}>{stat.label}</Text>
                                    <Text name='small' type='bold' color={colors.secondaryPurple} paddingBottom={0}>
                                        {stat.value >= 1000 ? `${(stat.value / 1000).toFixed(1)}k` : stat.value}
                                    </Text>
                                </StatRow>
                            ))}
                        </SideSection>
                    )}
                </RightSidebar>
            </ContentArea>
        </DetailContainer>
    );
}

// ── STYLED COMPONENTS ──────────────────────────────────────────────────────────

const DetailContainer = styled(Column)`
    width: 100%;
    background: ${colors.neutralBackground};
`;

// Header — hero + centered identity block
const Header = styled.div`
    width: 100%;
    background: ${colors.neutralLight};
`;

const HeroBanner = styled.div`
    width: 100%;
    height: 170px;
    position: relative;
    background: ${colors.secondaryPurple};
    overflow: hidden;

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        height: 130px;
    }
`;

const HeroImage = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.75;
`;

const HeroOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.50) 100%);
`;

const HeroActions = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
    z-index: 1;
`;

const ActionButton = styled.button`
    border: 1.5px solid rgba(255, 255, 255, 0.75);
    border-radius: 20px;
    padding: 5px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;
    background: rgba(0, 0, 0, 0.28);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    transition: background 0.2s;

    &:hover { background: rgba(0, 0, 0, 0.45); }
`;

const ActionLabel = styled.span`
    color: ${colors.neutralLight};
    font-size: 13px;
    font-weight: 500;

    @media screen and (max-width: calc(${breakpoints.sm} - 1px)) {
        display: none;
    }
`;

const HeaderBody = styled(Column)`
    align-items: center;
    padding: 0 20px 24px;
    background: ${colors.neutralLight};
`;

const AvatarWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: -44px;
    margin-bottom: 8px;
    z-index: 1;
`;

const AvatarRing = styled.div`
    width: 88px;
    height: 88px;
    border-radius: 50%;
    border: 4px solid ${colors.neutralLight};
    box-shadow: 0 0 0 3px ${({ isOpen }) => isOpen ? colors.success : colors.nightLight};
    overflow: hidden;
    background: ${colors.neutralLight};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const AvatarStatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 14px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${({ isOpen }) => isOpen ? colors.success : colors.nightLight};
    white-space: nowrap;
    margin-top: -14px;
    z-index: 2;
    position: relative;
`;

const HeaderName = styled.h2`
    font-size: 20px;
    font-weight: 700;
    color: ${colors.nightDark};
    text-align: center;
    margin: 6px 0 6px 0;
    line-height: 1.2;
`;

const HeaderMetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 6px;
`;

const RatingText = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${({ isOpen }) => isOpen ? colors.success : colors.nightLight};
`;

const AddressRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 10px;
    justify-content: center;
`;

const AddressText = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    text-align: center;
    line-height: 1.4;
`;

const TagsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: center;
    margin-top: 4px;
`;

const ClassificationChip = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
    text-transform: capitalize;

    ${({ isFree }) => isFree
        ? `background: rgba(39,183,93,0.10); color: ${colors.successDark};`
        : `background: rgba(159,22,219,0.10); color: ${colors.occasionalPurple};`
    }
`;

const AdulthoodChip = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
    background: ${colors.nightDark};
    color: ${colors.neutral};
`;

const TagChip = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 400;
    background: rgba(63,107,198,0.08);
    color: ${colors.primary};
    text-transform: capitalize;
`;

// Content layout
const ContentArea = styled.div`
    display: flex;
    align-items: flex-start;
    padding: 20px;
    gap: 20px;
    flex-wrap: wrap;

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        padding: 12px;
        flex-direction: column;
    }
`;

const LeftColumn = styled.div`
    flex: 1;
    min-width: 0;

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        width: 100%;
    }
`;

const RightSidebar = styled.div`
    width: 460px;
    min-width: 240px;
    flex-shrink: 0;

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        width: 100%;
        min-width: 0;
       
    }
`;

const Section = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
`;

const SectionTitleRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 14px;
`;

// Horizontal chips shared by features and service options
const ChipsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const FeatureChip = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 7px 13px;
    background: ${colors.neutralBackground};
    border-radius: 20px;
    border: 1px solid ${colors.nightLight}40;
`;

const ServiceChip = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 7px 13px;
    border-radius: 20px;

    ${({ selected }) => selected
        ? `background: rgba(39,183,93,0.06); border: 1px solid rgba(39,183,93,0.28);`
        : `background: rgba(0,0,0,0.02); border: 1px solid ${colors.nightLight}30;`
    }
`;

const ServiceDot = styled.span`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ selected }) => selected ? colors.success : colors.nightLight};
`;

const ChipLabel = styled.span`
    font-size: 13px;
    color: ${({ faded }) => faded ? colors.nightLight : colors.nightDark};
    line-height: 1;
    white-space: nowrap;
`;

// Products
const ProductsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 16px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const ProductCard = styled.div`
    display: flex;
    align-items: center;
    padding: 12px;
    border: 1px solid ${colors.nightLight};
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
`;

const ProductImage = styled.div`
    width: 56px;
    height: 56px;
    min-width: 56px;
    border-radius: 6px;
    overflow: hidden;
    background: ${colors.neutralDark};
    margin-right: 12px;

    img { width: 100%; height: 100%; object-fit: cover; }
`;

const ProductImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: ${colors.neutralDark};
`;

const ProductInfo = styled(Column)`
    flex: 1;
    min-width: 0;
`;

// Sidebar
const SideSection = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
`;

const ContactRow = styled.div`
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;

    &:last-child { margin-bottom: 0; }
`;

const ContactInfo = styled(Column)`
    margin-left: 12px;
    flex: 1;
    min-width: 0;
`;

const MapLink = styled.a`
    font-size: 12px;
    color: ${colors.secondaryPurple};
    text-decoration: underline;
    cursor: pointer;
    margin-top: 2px;
`;

const HoursRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 0;
    border-bottom: 1px solid ${colors.nightLight}40;

    &:last-child { border-bottom: none; }
`;

const DayLabel = styled.span`
    min-width: 36px;
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const SocialsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        grid-template-columns: 1fr;
    }
`;

const SocialRow = styled.div`
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border: 1px solid ${colors.nightLight};
    border-radius: 8px;
    cursor: pointer;
    text-decoration: none;
    transition: opacity 0.2s;
    min-width: 0;

    &:hover { opacity: 0.75; }
`;

const StatRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid ${colors.nightLight}40;

    &:last-child { border-bottom: none; }
`;

// Events section
const EventsScroll = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const EventItem = styled.a`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px;
    border: 1px solid ${colors.nightLight}40;
    border-radius: 10px;
    text-decoration: none;
    transition: background 0.15s ease;
    background: ${colors.neutralBackground};

    &:hover { background: ${colors.neutralDark}; }
`;

const EventThumb = styled.div`
    width: 64px;
    height: 64px;
    min-width: 64px;
    border-radius: 8px;
    overflow: hidden;
    background: ${colors.neutralDark};
    position: relative;
    flex-shrink: 0;

    img { width: 100%; height: 100%; object-fit: cover; }
`;

const EventTicketBadge = styled.div`
    position: absolute;
    bottom: 4px;
    right: 4px;
    background: ${colors.secondaryPurple};
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const EventItemInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
`;

const EventItemName = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 2px;
`;

const EventPriceBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
    align-self: flex-start;
    margin-top: 3px;
    margin-bottom: 4px;

    ${({ isFree }) => isFree
        ? `background: rgba(39,183,93,0.12); color: ${colors.successDark};`
        : `background: rgba(159,22,219,0.10); color: ${colors.occasionalPurple};`
    }
`;

const EventDateRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const EventDateText = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
`;

const MenuCTAButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 18px;
    padding: 12px 32px;
    background: ${colors.secondaryPurple};
    color: ${colors.neutralLight};
    border-radius: 24px;
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
    transition: opacity 0.18s;
    box-shadow: 0 2px 10px rgba(159, 22, 219, 0.22);

    &:hover { opacity: 0.85; }
`;
